/**
 * Estado de la «Primera travesía» guardado en las prefs sincronizadas del
 * presupuesto: `travesia` (JSON con el estado, el paso y lo ya creado) y
 * `travesia-faros` (avisos de una vez ya vistos). Así se retoma tras cerrar
 * la app y otro móvil con el mismo barco no la repite.
 */
import type { IntegerAmount } from '@actual-app/core/shared/util';

import { OPCIONES_VIVE } from './catalogo';
import type { GrupoTravesia, IdMeta, Vive } from './catalogo';

export type EstadoTravesiaValor = 'pendiente' | 'hecha' | 'saltada';

/** Lo que la travesía ya ha escrito (ids reales de Actual). */
export type IdsTravesia = {
  /** Se borraron las categorías de fábrica. */
  limpiado?: boolean;
  /** Grupos creados, por clave del catálogo. */
  grupos?: Partial<Record<GrupoTravesia, string>>;
  /** Categorías creadas: id del catálogo → id real. */
  categorias?: Record<string, string>;
  /** Cuentas creadas en el paso 5. */
  cuentas?: string[];
  /** Meta del paso 7: categoría real, clave del catálogo y cuota mensual. */
  meta?: { id: string; clave: IdMeta; cuota: IntegerAmount };
  /**
   * Paso 6, «Rumbo también se planifica»: la categoría Rumbo (Gavi la
   * reconoce por el id aunque la renombren) y si quedó sin apartar porque
   * aún no había dinero («Aún no tengo dinero»).
   */
  rumbo?: { id: string; pendiente?: boolean };
};

export type EstadoTravesia = {
  estado: EstadoTravesiaValor;
  /** Paso en curso (2..10). */
  paso: number;
  vive?: Vive;
  mascota?: boolean;
  hijos?: boolean;
  ids?: IdsTravesia;
  /**
   * Sin Gavi (se eligió en el astillero): se aplica «Saltar» en silencio,
   * como en pantalla ancha.
   */
  silencio?: boolean;
};

export const PRIMER_PASO = 2;

/**
 * Orden de los pasos.
 * - 2 (19-oct-2026): las categorías (3 y 4) van antes que las cuentas (5),
 *   para que Gavi pueda ordenar la historia del extracto en ellas.
 * - 3 (1-nov-2026): paso 6 nuevo, «Rumbo también se planifica»; el objetivo
 *   y lo que venía detrás corren un puesto (7-10).
 * Una travesía guardada con un orden de antes se retoma en el paso
 * equivalente.
 */
export const ORDEN_PASOS = 3;

/** Primer paso que corrió un puesto con el orden 3. */
const PASO_RUMBO = 6;

/** Paso guardado con el orden de antes → paso de ahora. */
export function pasoDeOrdenAntiguo(paso: number): number {
  if (paso === 4) {
    return 3;
  }
  if (paso === 5) {
    return 4;
  }
  return paso;
}

/** Paso guardado con el orden `orden` → paso de ahora. */
export function pasoDeOrden(paso: number, orden: unknown): number {
  if (orden === ORDEN_PASOS) {
    return paso;
  }
  const delOrden2 = orden === 2 ? paso : pasoDeOrdenAntiguo(paso);
  return delOrden2 >= PASO_RUMBO ? delOrden2 + 1 : delOrden2;
}

/** Paso final: Inicio con el lema. El 9 son las notificaciones. */
export const ULTIMO_PASO = 10;

const ESTADOS: readonly EstadoTravesiaValor[] = [
  'pendiente',
  'hecha',
  'saltada',
];

function esObjeto(v: unknown): v is Record<string, unknown> {
  return v != null && typeof v === 'object' && !Array.isArray(v);
}

function textos(v: unknown): Record<string, string> {
  if (!esObjeto(v)) {
    return {};
  }
  const res: Record<string, string> = {};
  for (const [k, x] of Object.entries(v)) {
    if (typeof x === 'string') {
      res[k] = x;
    }
  }
  return res;
}

function leerIds(v: unknown): IdsTravesia | undefined {
  if (!esObjeto(v)) {
    return undefined;
  }
  const ids: IdsTravesia = {};
  if (v.limpiado === true) {
    ids.limpiado = true;
  }
  if (esObjeto(v.grupos)) {
    ids.grupos = textos(v.grupos);
  }
  if (esObjeto(v.categorias)) {
    ids.categorias = textos(v.categorias);
  }
  if (Array.isArray(v.cuentas)) {
    ids.cuentas = v.cuentas.filter((x): x is string => typeof x === 'string');
  }
  const meta = v.meta;
  if (
    esObjeto(meta) &&
    typeof meta.id === 'string' &&
    typeof meta.clave === 'string' &&
    typeof meta.cuota === 'number'
  ) {
    ids.meta = {
      id: meta.id,
      clave: meta.clave as IdMeta,
      cuota: meta.cuota,
    };
  }
  const rumbo = v.rumbo;
  if (esObjeto(rumbo) && typeof rumbo.id === 'string') {
    ids.rumbo =
      rumbo.pendiente === true
        ? { id: rumbo.id, pendiente: true }
        : { id: rumbo.id };
  }
  return ids;
}

/** Pref `travesia` → estado. `null` si no hay travesía o no se entiende. */
export function leerTravesia(
  raw: string | null | undefined,
): EstadoTravesia | null {
  if (!raw) {
    return null;
  }
  let v: unknown;
  try {
    v = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!esObjeto(v) || !ESTADOS.includes(v.estado as EstadoTravesiaValor)) {
    return null;
  }
  const guardado =
    typeof v.paso === 'number' && Number.isFinite(v.paso)
      ? Math.max(PRIMER_PASO, Math.round(v.paso))
      : PRIMER_PASO;
  const paso = Math.min(ULTIMO_PASO, pasoDeOrden(guardado, v.orden));
  const res: EstadoTravesia = {
    estado: v.estado as EstadoTravesiaValor,
    paso,
  };
  if (OPCIONES_VIVE.includes(v.vive as Vive)) {
    res.vive = v.vive as Vive;
  }
  if (typeof v.mascota === 'boolean') {
    res.mascota = v.mascota;
  }
  if (typeof v.hijos === 'boolean') {
    res.hijos = v.hijos;
  }
  const ids = leerIds(v.ids);
  if (ids) {
    res.ids = ids;
  }
  if (v.silencio === true) {
    res.silencio = true;
  }
  return res;
}

/** Estado → pref `travesia` (siempre con el orden de pasos de ahora). */
export function escribirTravesia(estado: EstadoTravesia): string {
  return JSON.stringify({ ...estado, orden: ORDEN_PASOS });
}

/**
 * Mezcla cambios en la pref `travesia` (los `ids` se mezclan, no se
 * sustituyen). Sin estado previo parte de `pendiente` en el paso 2.
 */
export function actualizarTravesia(
  raw: string | null | undefined,
  cambios: Partial<Omit<EstadoTravesia, 'ids'>> & { ids?: IdsTravesia },
): string {
  const previo = leerTravesia(raw) ?? {
    estado: 'pendiente' as const,
    paso: PRIMER_PASO,
  };
  const { ids, ...resto } = cambios;
  const siguiente: EstadoTravesia = { ...previo, ...resto };
  if (ids) {
    const p = previo.ids ?? {};
    siguiente.ids = {
      ...p,
      ...ids,
      grupos: { ...p.grupos, ...ids.grupos },
      categorias: { ...p.categorias, ...ids.categorias },
      cuentas: [...(p.cuentas ?? []), ...(ids.cuentas ?? [])].filter(
        (x, i, a) => a.indexOf(x) === i,
      ),
    };
  }
  return escribirTravesia(siguiente);
}

/** Pref `travesia` inicial que deja el Astillero al botar un barco nuevo. */
export function travesiaNueva(): string {
  return escribirTravesia({ estado: 'pendiente', paso: PRIMER_PASO });
}

/**
 * Pref `travesia` para volver a hacer la travesía desde Ajustes: pendiente
 * en el paso 2 y sin los ids de la anterior (si no, no volvería a crear
 * cuentas ni objetivo). Las categorías de fábrica solo se borran si el
 * barco nunca tuvo travesía y aún no tiene movimientos; si no, se da la
 * limpieza por hecha para no tocar lo que ya se usa.
 */
export function travesiaDeNuevo(
  raw: string | null | undefined,
  hayMovimientos: boolean,
): string {
  const limpiar = leerTravesia(raw) === null && !hayMovimientos;
  return escribirTravesia({
    estado: 'pendiente',
    paso: PRIMER_PASO,
    ...(limpiar ? {} : { ids: { limpiado: true } }),
  });
}

/** La travesía está en curso (hay que enseñarla). */
export function travesiaPendiente(estado: EstadoTravesia | null): boolean {
  return estado?.estado === 'pendiente';
}

// ---------------------------------------------------------------------------
// Faros
// ---------------------------------------------------------------------------

export type Faro = 'gastos' | 'cuentas';
const FAROS: readonly Faro[] = ['gastos', 'cuentas'];

/** Pref `travesia-faros` → faros ya vistos. */
export function leerFaros(raw: string | null | undefined): Faro[] {
  if (!raw) {
    return [];
  }
  try {
    const v: unknown = JSON.parse(raw);
    return Array.isArray(v) ? FAROS.filter(f => v.includes(f)) : [];
  } catch {
    return [];
  }
}

/** Marca un faro como visto. */
export function marcarFaro(raw: string | null | undefined, faro: Faro): string {
  const vistos = leerFaros(raw);
  return JSON.stringify(vistos.includes(faro) ? vistos : [...vistos, faro]);
}
